import React, { useEffect, useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import { useDashboardData } from "../../hooks/api/useDashboardData";
import DashboardHeader from "./DashboardHeader";
import SalesVsInventoryChart from "./charts/SalesVsInventoryChart";
import ProductItemsByStateBarChart from "./charts/ProductItemsByStateBarChart";
import DistributionPieChart from "./charts/DistributionPieChart";
import ComparisonBarChart from "./charts/ComparisonBarChart";
import DashboardSectionAccordion from "./DashboardSectionAccordion";
import useMediaQuery from "@mui/material/useMediaQuery";
import MetricLedger from "../common/MetricLedger/MetricLedger";
import MetricCard from "../common/MetricLedger/MetricCard";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import { getMetricPeriodLabel } from "./utils/metricsUtils";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  setDashboardStoreId,
  setMetricTimeRange
} from "../../store/slices/dashboardSlice";
import { MetricTimeRange } from "./types/dashboardTypes";
import { UserRole } from "../Profile/enums/profileEnums";
import { useGetCompanyStores } from "../../hooks/api/useStore";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import { useOrganizationLabels } from "../../hooks/useOrganizationLabels";
import { isDashboardSectionVisible } from "../../config/organizationVerbiageConfig";
import EducationDashboardCards from "../Education/EducationDashboardCards";
import OverdueInvoicesCard from "../Education/Billing/OverdueInvoicesCard";

import { buildLogContext, captureAppException } from "../../Utils/appLogger";

const chartVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.1 + i * 0.08,
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  })
};

const SHOW_SALES_INVENTORY_SECTION = false;

const DashboardPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const org = useOrganizationLabels();
  const showSection = (
    section: Parameters<typeof isDashboardSectionVisible>[1]
  ) => isDashboardSectionVisible(org.dashboard, section);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const storeList = useSelector((state: RootState) => state.store.storeList);
  const dashboardStoreId = useSelector(
    (state: RootState) => state.dashboard.dashboardStoreId
  );
  const isCompanyAdmin =
    String(userDetails?.role ?? "").trim() === UserRole.COMPANY_ADMIN;
  /** Company admin picked one store in the header filter — analytics below should be store-scoped where APIs support it. */
  const companyAdminStoreFilterActive =
    isCompanyAdmin && Boolean(dashboardStoreId?.trim());
  const getCompanyStores = useGetCompanyStores();

  useEffect(() => {
    if (!isCompanyAdmin || !userDetails?.companyId) return;
    (async () => {
      try {
        await getCompanyStores.mutateAsync({
          companyId: userDetails.companyId
        });
      } catch (e) {
        captureAppException(
          "DashboardPage: failed to load stores for filter",
          e,
          buildLogContext(
            "dashboard",
            "get-company-stores",
            userDetails?.id,
            userDetails?.companyId,
            userDetails?.storeId
          )
        );
      }
    })();
  }, [isCompanyAdmin, userDetails?.companyId]);

  const storeFilterOptions = useMemo(
    () =>
      (storeList ?? []).map((s) => ({
        id: s.id,
        name: s.name
      })),
    [storeList]
  );

  const { data, isLoading, error } = useDashboardData();
  const {
    kpis = [],
    subscriptionKpis = [],
    orderKpis = [],
    storeKpis = [],
    supplierKpis = [],
    customerKpis = [],
    employeeKpis = [],
    productKpis = [],
    categoryKpis = [],
    movement = [],
    employeesByStatus = [],
    employeesByRole = [],
    storesByStatus = [],
    suppliersByStatus = [],
    customersByType = [],
    ordersByStatus = [],
    invoicesByStatus = [],
    productsByStatus = [],
    categoriesByStatus = [],
    salesAndInventoryTrend = []
  } = data ?? {};
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  const metricTimeRangeFromState = useSelector(
    (state: RootState) => state.dashboard
  ).metricTimeRange;

  const metricTimeRange: MetricTimeRange = useMemo(
    () => ({
      startDate: new Date(metricTimeRangeFromState.startDate),
      endDate: new Date(metricTimeRangeFromState.endDate)
    }),
    [metricTimeRangeFromState.startDate, metricTimeRangeFromState.endDate]
  );

  const periodLabel = getMetricPeriodLabel(
    metricTimeRange.startDate,
    metricTimeRange.endDate,
    t
  );

  if (isLoading) {
    return (
      <CommonLoader
        loadingMessage={t("dashboard-loading-message", "Loading dashboard...")}
      />
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 320,
          p: 3,
          textAlign: "center"
        }}
      >
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          {t("dashboard-load-error-title", "Unable to load your dashboard")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(
            "dashboard-load-error-body",
            "Please refresh the page or try again in a moment."
          )}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        width: "100%",
        maxWidth: 1200,
        mx: "auto",
        p: matchMD ? 2 : 3,
        pt: { xs: 2, sm: 3 },
        overflowX: "hidden",
        boxSizing: "border-box"
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
            {t("dashboard-title", "Dashboard")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {org.dashboardSubtitle}
          </Typography>
        </Box>
        <DashboardHeader
          onRangeChange={(r) => dispatch(setMetricTimeRange(r))}
          metricRange={metricTimeRange}
          storeFilter={
            org.dashboard.showStoreSections &&
            isCompanyAdmin &&
            storeFilterOptions.length > 0
              ? {
                  stores: storeFilterOptions,
                  value: dashboardStoreId,
                  onChange: (id) => dispatch(setDashboardStoreId(id))
                }
              : undefined
          }
        />
      </motion.div>

      {showSection("revenueKpis") && (
        <MetricLedger kpis={kpis} period={periodLabel} />
      )}

      {showSection("educationSummary") && (
        <DashboardSectionAccordion
          title={t("dashboard-section-school-operations", "School operations")}
          defaultExpanded={true}
        >
          <EducationDashboardCards />
          <OverdueInvoicesCard />
        </DashboardSectionAccordion>
      )}

      {showSection("subscription") && (
        <DashboardSectionAccordion
          title={t("dashboard-section-subscription", "Subscription")}
          defaultExpanded={true}
        >
          <MetricLedger
            kpis={subscriptionKpis}
            period={periodLabel}
            columns={{ xs: 1, sm: 2, md: 3, lg: 3 }}
          />
        </DashboardSectionAccordion>
      )}

      {showSection("orders") && (
        <DashboardSectionAccordion
          title={t("dashboard-section-orders", "Orders")}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <MetricLedger
              kpis={orderKpis}
              period={periodLabel}
              columns={{ xs: 1, sm: 2, md: 2, lg: 4 }}
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  lg: "repeat(2, minmax(0, 1fr))"
                },
                gap: 2,
                alignItems: "stretch"
              }}
            >
              <ComparisonBarChart
                title={t("dashboard-orders-by-status", "Orders by status")}
                data={ordersByStatus}
                emptyMessage={t(
                  "dashboard-orders-no-status-data",
                  "No order status data"
                )}
              />
              <ComparisonBarChart
                title={t("dashboard-invoices-by-status", "Invoices by status")}
                data={invoicesByStatus}
                emptyMessage={t(
                  "dashboard-invoices-no-status-data",
                  "No invoice status data"
                )}
              />
            </Box>
          </Box>
        </DashboardSectionAccordion>
      )}

      {showSection("salesInventory") && SHOW_SALES_INVENTORY_SECTION && (
        <DashboardSectionAccordion
          title={t("dashboard-section-sales-inventory", "Sales & inventory")}
        >
          <motion.div
            custom={0}
            variants={chartVariants}
            initial="hidden"
            animate="visible"
            style={{ width: "100%" }}
          >
            <SalesVsInventoryChart
              metricData={salesAndInventoryTrend}
              metricRange={metricTimeRange}
            />
          </motion.div>
        </DashboardSectionAccordion>
      )}

      {showSection("products") && (
        <DashboardSectionAccordion
          title={t("dashboard-section-products", "Products")}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)"
              },
              gap: 2,
              alignItems: "flex-start"
            }}
          >
            {productKpis.slice(0, 2).map((kpi, i) => (
              <MetricCard
                key={kpi.id}
                kpiMetric={kpi}
                period={periodLabel}
                index={i}
              />
            ))}
            <DistributionPieChart
              title={t(
                "dashboard-pie-product-items-by-state",
                "Product items by state"
              )}
              data={movement.filter((d) => d.value > 0)}
              emptyMessage={t(
                "dashboard-pie-no-product-item-data",
                "No product item data"
              )}
            />
            <DistributionPieChart
              title={t(
                "dashboard-pie-products-by-status",
                "Products by status"
              )}
              data={productsByStatus}
              emptyMessage={t(
                "dashboard-pie-no-product-status-data",
                "No product status data"
              )}
            />
          </Box>
        </DashboardSectionAccordion>
      )}

      {showSection("productItemsByState") && (
        <DashboardSectionAccordion
          title={t(
            "dashboard-section-product-items-by-state",
            "Product items by state"
          )}
        >
          <motion.div
            custom={1}
            variants={chartVariants}
            initial="hidden"
            animate="visible"
            style={{ width: "100%" }}
          >
            <ProductItemsByStateBarChart data={movement} />
          </motion.div>
        </DashboardSectionAccordion>
      )}

      {showSection("categories") && (
        <DashboardSectionAccordion
          title={t("dashboard-section-categories", "Categories")}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)"
              },
              gap: 2,
              alignItems: "flex-start"
            }}
          >
            {categoryKpis.slice(0, 1).map((kpi, i) => (
              <MetricCard
                key={kpi.id}
                kpiMetric={kpi}
                period={periodLabel}
                index={i}
              />
            ))}
            <DistributionPieChart
              title={t(
                "dashboard-pie-categories-by-status",
                "Categories by status"
              )}
              data={categoriesByStatus}
              emptyMessage={t(
                "dashboard-pie-no-category-data",
                "No category data"
              )}
            />
          </Box>
        </DashboardSectionAccordion>
      )}

      {showSection("stores") &&
        org.dashboard.showStoreSections &&
        !companyAdminStoreFilterActive && (
          <DashboardSectionAccordion
            title={t("dashboard-section-stores", "Stores")}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)"
                },
                gap: 2,
                alignItems: "flex-start"
              }}
            >
              {storeKpis.slice(0, 1).map((kpi, i) => (
                <MetricCard
                  key={kpi.id}
                  kpiMetric={kpi}
                  period={periodLabel}
                  index={i}
                />
              ))}
              <DistributionPieChart
                title={t("dashboard-pie-stores-by-status", "Stores by status")}
                data={storesByStatus}
                emptyMessage={t(
                  "dashboard-pie-no-store-status-data",
                  "No store status data"
                )}
              />
            </Box>
          </DashboardSectionAccordion>
        )}

      {showSection("staff") && (
        <DashboardSectionAccordion title={org.dashboardStaffSectionTitle}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)"
              },
              gap: 2,
              alignItems: "flex-start"
            }}
          >
            {employeeKpis.slice(0, 1).map((kpi, i) => (
              <MetricCard
                key={kpi.id}
                kpiMetric={kpi}
                period={periodLabel}
                index={i}
              />
            ))}
            <DistributionPieChart
              title={org.dashboardStaffByStatusTitle}
              data={employeesByStatus}
              emptyMessage={t(
                "dashboard-pie-no-employee-status-data",
                "No employee status data"
              )}
            />
            <DistributionPieChart
              title={org.dashboardStaffByRoleTitle}
              data={employeesByRole}
              emptyMessage={t("dashboard-pie-no-role-data", "No role data")}
            />
          </Box>
        </DashboardSectionAccordion>
      )}

      {showSection("customers") && (
        <DashboardSectionAccordion
          title={t("dashboard-section-customers", "Customers")}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)"
              },
              gap: 2,
              alignItems: "flex-start"
            }}
          >
            {(customerKpis.length >= 6
              ? [customerKpis[0], customerKpis[1], customerKpis[5]]
              : customerKpis.slice(0, 3)
            ).map((kpi, i) => (
              <MetricCard
                key={kpi.id}
                kpiMetric={kpi}
                period={periodLabel}
                index={i}
              />
            ))}
            <DistributionPieChart
              title={t("dashboard-pie-customers-by-type", "Customers by type")}
              data={customersByType}
              emptyMessage={t(
                "dashboard-pie-no-customer-type-data",
                "No customer type data"
              )}
            />
          </Box>
        </DashboardSectionAccordion>
      )}

      {showSection("suppliers") && (
        <DashboardSectionAccordion
          title={t("dashboard-section-suppliers", "Suppliers")}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)"
              },
              gap: 2,
              alignItems: "flex-start"
            }}
          >
            {supplierKpis.slice(0, 1).map((kpi, i) => (
              <MetricCard
                key={kpi.id}
                kpiMetric={kpi}
                period={periodLabel}
                index={i}
              />
            ))}
            <DistributionPieChart
              title={t(
                "dashboard-pie-suppliers-by-status",
                "Suppliers by status"
              )}
              data={suppliersByStatus}
              emptyMessage={t(
                "dashboard-pie-no-supplier-status-data",
                "No supplier status data"
              )}
            />
          </Box>
        </DashboardSectionAccordion>
      )}
    </Box>
  );
};

export default DashboardPage;
